import { app } from '@/main';
import { Route, RouteRecord } from 'vue-router';
import { RouteMeta } from '@/router/route.model';
import AuthorizationService from '@/api/AuthorizationService';
import store from '@/store';
import APIService from '@/api';
import {
  openCenterModal,
  openDefaultModal,
  showModalCoinNetworkBilling
} from '@/utils/modal';
import { InitialAdminPath, InitialSuperAdminPath } from '@/constants/routes';
import { middlewares } from './middlewares';
import {
  removeExternalOauthSession,
  removeMerchantMeetingOboardingState,
  setExternalOauthSession
} from './router-utils';
import StoreInitializer from '@/store/initializer';
import JwtVerification from '@/services/JwtVerification';
import AlertModals from '@/services/AlertModals';
import { MiddlewareCallback } from './middleware.interface';
import eventBus from '@/utils/eventBus';
import { handleZendeskByRoute } from '../utils/zendesk';
import { insideProviderIframe } from '../utils/providerIframe';
import { saveIframeData } from '../utils/merchantoauth';
import { resetElementScroll } from '@/utils/resetElementScroll';
import ModalOptions, { ModalOptionsV2 } from '@/components/Modal/ModalOptions';
import segmentIo from '@/utils/segment.io';
import { hasUTMQueryParams } from '@/utils/utm-methods';
import FeedbackService from '@/api/FeedbackService';
import flagKeys from '@/constants/flagKeys';
import { NOTIFICATION_MESSAGE_STORAGE } from '@/models/notification';
import { IBusinessType } from '@/models/business';
import { isBothProvider } from '@/utils/provider';
import { EBillingStatusTypes } from '@/store/modules/businessTypes';
import { EUtmParamsEvent, UTM_DATA_STORAGE } from '@/models/segment.io';
import { EOnboardingMeetingStatus } from '@/models/onboarding-call';
import BusinessService from '@/api/BusinessService';
import isRemoteLoading from '@/utils/remote-loading';

interface AuthUserProps {
  hasRootCredentials: boolean;
  hasManagerCredentials: boolean;
  superRootPage: boolean;
  meta: RouteMeta;
  from: Route;
  next: Function;
}

interface UnauthenticatedUserProps {
  isRootView: Function;
  superRootPage: boolean;
  requiresAuth: boolean;
  meta: RouteMeta;
  to: Route;
  from: Route;
  next: Function;
}

const service = new AuthorizationService();

function isRootUser() {
  return store.getters['business/isRootUser'];
}

function isRootView() {
  return store.getters['business/isRootView'];
}

function getFlags() {
  return store.getters['flags/getFlags'];
}

function RunAuthRedirects(to: Route, from: Route, next: Function): void {
  const [{ meta }] = to.matched;
  const requiresAuth = to.matched.some(
    (record: RouteRecord) => record.meta.requiresAuth
  );
  const superRootPage = to.matched.some(
    (record: RouteRecord) => record.meta.superRoot
  );

  const requireBusiness: boolean = store.getters['manager/getRequireBusiness'];

  const routeRequireBusiness = to.matched.every(
    (record: RouteRecord) => record.meta.requiresBusiness
  );
  const routeRequiresAuthFree = to.matched.some(
    (record: RouteRecord) => record.meta.requiresAuthFree
  );

  const hasRootCredentials = isRootUser();
  const hasManagerCredentials = !!APIService.token && !isRootUser();

  service.userIsValid().then((userAuthenticated: boolean) => {
    if (to.name) {
      if (to.name.toLowerCase() === 'recovery' && hasManagerCredentials) {
        APIService.resetCredentials();
      }
    }

    if (userAuthenticated) {
      if (requireBusiness && routeRequireBusiness) {
        return window.location.assign('/manager');
      }

      if (!requiresAuth && !routeRequiresAuthFree) {
        handleAuthRules(to, next);
        return;
      }

      if (!requiresAuth && routeRequiresAuthFree) {
        return next();
      }

      if (requiresAuth) {
        handleAuthenticatedUser({
          hasRootCredentials,
          hasManagerCredentials,
          superRootPage,
          meta,
          from,
          next
        });
        return;
      }
    }

    handleUnauthenticatedUser({
      isRootView,
      superRootPage,
      requiresAuth,
      meta,
      to,
      from,
      next
    });
  });
}

function handleAuthRules(to: Route, next: Function): void {
  if (to.meta && to.meta.authRules && to.meta.authRules.authType) {
    const { authType } = to.meta.authRules;
    const currentAuthType = isRootUser() ? 'super-admin' : 'manager';

    if (authType.includes(currentAuthType)) {
      return next();
    }

    window.location.assign(
      authType.includes('manager') ? InitialSuperAdminPath : InitialAdminPath()
    );
  } else {
    window.location.assign(
      isRootUser() ? InitialSuperAdminPath : InitialAdminPath()
    );
  }
}

function handleAuthenticatedUser({
  hasRootCredentials,
  hasManagerCredentials,
  superRootPage,
  meta,
  from,
  next
}: AuthUserProps): void {
  if (hasRootCredentials) {
    if (superRootPage) return next();
    if (!(hasManagerCredentials || store.getters['manager/isImpersonated'])) {
      if (isRemoteLoading()) {
        return window.parent.location.assign('/');
      } else {
        return window.location.assign('/');
      }
    }
  }

  if (!hasRootCredentials && superRootPage) {
    return window.location.assign('/admin');
  }

  if (!superRootPage) {
    if (meta.flag && !store.getters['flags/getFlags'].includes(meta.flag)) {
      return window.location.assign(InitialAdminPath());
    }

    if (
      meta.requiresBusinessType &&
      meta.requiresBusinessType.length &&
      !meta.requiresBusinessType.includes(
        store.getters['business/getBusinessType']
      )
    ) {
      return next(from.path);
    }

    if (
      meta.roles ||
      (meta.navbar && meta.navbar.group && meta.navbar.group.roles)
    ) {
      const managerRole = store.getters['business/getManagerRole'];
      const roleIncluded =
        meta.roles &&
        (meta.roles.includes(managerRole) ||
          (meta.navbar &&
            meta.navbar.group &&
            meta.navbar.group.roles.includes(managerRole)));

      if (!roleIncluded) {
        return next(from.path);
      }
    }
    next();
  }
}

function handleUnauthenticatedUser({
  isRootView,
  superRootPage,
  requiresAuth,
  meta,
  to,
  next
}: UnauthenticatedUserProps): void {
  if (isRootView() && superRootPage && requiresAuth) {
    location.href = '/admin';
    return;
  }

  if (requiresAuth) {
    return app.$router.push({
      path: '/',
      ...(hasUTMQueryParams(to) && {
        query: { ...to.query, redirect_to: to.path }
      })
    });
  }

  if (meta.flag && !store.getters['flags/getFlags'].includes(meta.flag)) {
    return app.$router.push('/login');
  }
  next();
}

const afterEach = async (to: Route, from: Route) => {
  eventBus.$emit('hideLoader');

  if (to.meta.pageView && to.name !== from.name && !isRemoteLoading()) {
    const eventLabel =
      typeof to.meta.pageView.eventLabel === 'function'
        ? to.meta.pageView.eventLabel()
        : to.meta.pageView.eventLabel;

    segmentIo.page(eventLabel, {
      ...(to.meta.pageView.eventProps && to.meta.pageView.eventProps(to)),
      'From Page Name': from.name || '',
      'From Page Url': from.path || '',
      ...(to.query && to.query.tab ? { tab: to.query.tab } : {})
    });
  }

  if (to.matched.some(m => m.meta.scrollToTop)) {
    window.scrollTo(0, 0);
    const signup = document.querySelector('.signup__body');
    if (signup) signup.scrollTo(0, 0);
  }
  const businessInfo = store.getters['business/getBusinessInfo'];

  if (
    window.zE &&
    businessInfo &&
    businessInfo.business &&
    businessInfo.business.loggedInManager
  ) {
    const { fullname, mail }: any = businessInfo.business.loggedInManager;
    const name = `${fullname} (${businessInfo.business.name})`;
    if ((window.zE as any).identify)
      (window.zE as any).identify({
        name,
        email: mail
      });
    try {
      window.zE('webWidget', 'prefill', {
        name: {
          value: name,
          readOnly: true // optional
        },
        email: {
          value: mail,
          readOnly: true // optional
        }
      });
    } catch {
      // IGNORE ZENDESK ERROR
    }
  }

  await updateOnboardingMeetingStore(to);
  if (!isRemoteLoading()) {
    await showModalOnChangeRouter(to);
  }
};

const updateOnboardingMeetingStore = async (to: Route) => {
  const businessInfo = store.getters['business/getBusinessInfo']
    ? store.getters['business/getBusinessInfo'].business
    : {};

  const businessService = new BusinessService();

  if (
    to.meta &&
    to.meta.navbar &&
    businessInfo &&
    businessInfo.onboardingMeeting &&
    businessInfo.onboardingMeeting.status !== EOnboardingMeetingStatus.PENDING
  ) {
    removeMerchantMeetingOboardingState();
  }

  if (
    to.meta &&
    to.meta.navbar &&
    businessInfo &&
    businessInfo.onboardingMeeting &&
    (businessInfo.onboardingMeeting.status ===
      EOnboardingMeetingStatus.SCHEDULED ||
      businessInfo.onboardingMeeting.status ===
        EOnboardingMeetingStatus.RESCHEDULED)
  ) {
    const { data } = await businessService.getBusinessInfo();
    data.manager = data.loggedInManager;
    store.dispatch('business/SET_BUSINESS_INFO', data);
  }
};

const showModalOnChangeRouter = async (to: Route) => {
  const businessInfo = store.getters['business/getBusinessInfo'];

  const currentProvider =
    store.getters['merchant/getConnectedProviderWithWidgetSettings'];

  const showFeedbackModal =
    currentProvider &&
    store.getters['flags/getFlags'].includes(flagKeys.feedbackPlatform) &&
    businessInfo &&
    businessInfo.business &&
    !to.path.includes('business-onboarding');

  if (showFeedbackModal) {
    const service = new FeedbackService();
    const response = await service.getFeedback();
    if (response) {
      const business = {
        ...businessInfo.business,
        hasSendFeedback: !!response.data
      };
      eventBus.$emit('setBusinessInfo', business);
      store.dispatch('business/SET_BUSINESS_INFO', business);
    }
  }

  const businessHasFlag = (name: string) =>
    businessInfo &&
    businessInfo.business &&
    Object.keys(businessInfo.business).includes(name);

  if (showModalCoinNetworkBilling()) {
    if (
      businessInfo.business.subscriptionStatus ===
      EBillingStatusTypes.DELAY_PERIOD
    )
      openDefaultModal('coinNetworkWelcomeModal', {
        subscription: store.getters['business/getBillingSubscription'],
        ctaClick: () => {
          segmentIo.track('Clicked Billing Welcome Guided Tour');
          eventBus.$emit('startGuidedTour', 'billingGuidedTour');
        }
      });
    else openDefaultModal('coinNetworkNewsModal');
  } else if (
    businessHasFlag('seenInStoreOnlineRewardWhatsNew') &&
    !businessInfo.business.seenInStoreOnlineRewardWhatsNew &&
    businessInfo.business.coinRewardPercentage !== '0.00' &&
    currentProvider &&
    isBothProvider(currentProvider.name) &&
    store.getters['flags/getFlags'].includes(flagKeys.addWixMultipleIntegration)
  ) {
    openCenterModal('onlineInStoreModal', {
      mobilePositionBottom: true
    });
  } else if (
    businessHasFlag('seenWhatsNew') &&
    !businessInfo.business.seenWhatsNew &&
    store.getters['flags/getFlags'].includes(flagKeys.whatIsNew)
  ) {
    openCenterModal('whatIsNewModal', {
      mobilePositionBottom: true
    });
  } else if (
    businessHasFlag('showPlatformFeedback') &&
    businessInfo.business.showPlatformFeedback &&
    showFeedbackModal &&
    to.name !== 'feedback-platform'
  ) {
    eventBus.$emit(
      'openSubModal',
      new ModalOptionsV2({
        componentToRender: 'feedbackModal',
        hideCancelButton: false,
        icon: '',
        mobilePositionBottom: true,
        mobileFullScreen: false
      })
    );
  }
};

declare global {
  interface Window {
    previousRouteName?: string;
    zE?: (target: string | Object, channel?: string, config?: any) => void;
  }
}

const beforeEach = async (to: Route, from: Route, next: MiddlewareCallback) => {
  window.previousRouteName = from.name;

  resetElementScroll({
    selector: 'container-scrollable',
    selectorBy: 'className'
  });

  await service.userIsValid();

  if (
    from.name === 'home' &&
    !to.query['new-business'] &&
    to.path.includes('business-onboarding')
  ) {
    next(from);
    return;
  }

  if (!from.name || !to.meta.disableFullLoader) {
    eventBus.$emit('hideLoader');
    eventBus.$emit('showLoaderWithLocker', true);
  }

  if (window.zE) {
    /**
     * Update Zendesk suggestions
     */
    const searchTerms = to.path
      .split('/')
      .join(' ')
      .trim();
    try {
      window.zE('webWidget', 'helpCenter:setSuggestions', {
        search: searchTerms
      });
    } catch (error) {
      // IGNORE ZENDESK ERROR
    }
  }

  const keywords = Object.values(EUtmParamsEvent);
  let utmData: any = {};

  for (const keyword of keywords) {
    if (to.query && to.query[keyword]) {
      utmData[keyword] = to.query[keyword];
    }
  }

  if (Object.keys(utmData).length) {
    sessionStorage.setItem(UTM_DATA_STORAGE, JSON.stringify(utmData));
  }

  handleZendeskByRoute(to);

  /**
   * Login the manager via query token
   * if is `login-with-token` route
   */
  if (to.name === 'login-with-token') {
    const token = to.query.token as string;
    removeExternalOauthSession();
    if (token) {
      try {
        await service.exchangeUrlTokenForCookie(token);
        if (insideProviderIframe()) {
          saveIframeData(to.query);
        }
      } catch {
        // Invalid or expired URL token — send the user back to login.
        return next('/');
      }
      // Strip the token from the URL so it doesn't leak via referrer or
      // browser history on the next navigation.
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      window.history.replaceState(null, '', url.toString());
    }

    if (
      to.query.onlineMerchantWidgetStep ||
      to.query.onlineMerchantRewardStep
    ) {
      setExternalOauthSession(IBusinessType.ONLINE);
      return next({
        path: '/manager',
        query: {
          onlineMerchantWidgetStep: to.query.onlineMerchantWidgetStep,
          onlineMerchantRewardStep: to.query.onlineMerchantRewardStep
        }
      });
    }

    return next('/home');
  }

  eventBus.$emit('beforeEachRoute', to);

  if (to.query.refreshToken) {
    const authType = isRootUser() ? 'super-admin' : 'manager';

    const { status, data } = await service.changeToken(authType);

    if (status === 200) {
      service.setCredentials(data.access_token);

      const query = Object.assign({}, to.query);
      delete query.refreshToken;

      app.$router.replace({
        path: to.path,
        query
      });
    }
  }

  /**
   * Initialize each store in order of the defined modules
   * specified on src/store/index
   */
  await StoreInitializer.emitEvent('BeforeEachRoute', to);

  /**
   * Add or remove responsive meta-tag according to
   * `isResponsive` prop from route configuration
   */
  // if (
  //   (to.meta &&
  //     typeof to.meta.isResponsive === 'boolean' &&
  //     to.meta.isResponsive) ||
  //   getFlags().includes(to.meta.isResponsive)
  // ) {
  //   const tag = document.createElement('meta');
  //   tag.setAttribute('name', 'viewport');
  //   tag.setAttribute(
  //     'content',
  //     'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0'
  //   );
  //   document.head.appendChild(tag);
  // } else {
  //   const item = document.head.querySelector('meta[name=viewport]');
  //   item && item.remove();

  //   const tag = document.createElement('meta');
  //   tag.setAttribute('name', 'viewport');
  //   tag.setAttribute(
  //     'content',
  //     'width=1440, initial-scale=0, user-scalable=yes'
  //   );
  //   document.head.appendChild(tag);
  // }

  const tag = document.createElement('meta');
  tag.setAttribute('name', 'viewport');
  tag.setAttribute(
    'content',
    'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0'
  );
  document.head.appendChild(tag);

  /**
   * If the route depends on a feature flag and
   * that flag is deactivated, then prevent
   * the user from accessing the route
   */
  if (
    (to.meta.flag && !store.getters['flags/getFlags'].includes(to.meta.flag)) ||
    (to.meta.customFlagValidator &&
      !to.meta.customFlagValidator() &&
      to.meta.requiresAuth &&
      APIService.token)
  ) {
    app.$notification(
      "You don't have permission to access this page.",
      'error'
    );
    next(`/${to.meta.superRoot ? 'admin/dashboard' : ''}`);
    return;
  }

  /**
   * Show banners and alerts when the manager access the dashboard
   */
  AlertModals.initialize(to);

  /**
   * Verifies if JWT manager/business ids are according
   * to what we have in the business store module
   */
  JwtVerification.initialize(from, to);

  const sections = store.getters['business/getWidgetWarnChanges'];

  if (
    from.meta.warnChanges &&
    sections &&
    sections.length > 0 &&
    !to.params.discard
  ) {
    eventBus.$emit(
      'openSubModal',
      new ModalOptions(
        ``,
        ``,
        'none',
        true,
        ``,
        ``,
        ``,
        false,
        'discardChanges',
        false,
        true,
        true,
        true
      ),
      { to, sections }
    );

    eventBus.$emit('hideLoader');
    next(false);
    return;
  }

  const notificationMessage = localStorage.getItem(
    NOTIFICATION_MESSAGE_STORAGE
  );

  if (notificationMessage) {
    app.$notification(notificationMessage, 'success');
    localStorage.removeItem(NOTIFICATION_MESSAGE_STORAGE);
  }

  /**
   * Run middlewares if there's any active
   */
  const activeMiddlewares = middlewares.filter(md => md.canActivate(to, from));

  if (activeMiddlewares.length > 0) {
    for (const middleware of activeMiddlewares) {
      await middleware.execute(to, from, next);
    }
    return;
  }

  /**
   * General (old) redirects according to the
   * user current auth status
   */
  RunAuthRedirects(to, from, next);
};

export { beforeEach, afterEach };
